import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <main className="min-h-screen py-12 px-4 md:px-8">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-2 text-sm text-kawaii-textSoft hover:text-kawaii-text mb-8 font-body"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
          Back to my calendar
        </Link>

        <header className="mb-10">
          <p className="text-[10px] uppercase tracking-[0.3em] text-kawaii-textSoft font-bold">Legal</p>
          <h1 className="font-heading text-4xl md:text-5xl text-kawaii-text mt-2">Terms of Service</h1>
          <p className="text-xs text-kawaii-textSoft mt-3 font-body">Last updated: July 2026</p>
        </header>

        <article className="kawaii-card p-8 md:p-10 space-y-8 font-body text-kawaii-text leading-relaxed">
          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">1. Acceptance of these terms</h2>
            <p className="text-sm">
              By creating an account or using MyLymphieLife ("the app"), you agree to these Terms of Service.
              If you don't agree, please don't use the app.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">2. Not medical advice</h2>
            <p className="text-sm">
              MyLymphieLife is a <strong>personal wellness and symptom-tracking tool</strong>. It does <strong>not</strong> provide medical advice,
              diagnosis, or treatment. Any information generated by the app — including AI summaries, insights from Lymphie,
              or emergency scripts — is for informational and organisational purposes only.
              Always seek the advice of a qualified physician or your local emergency service for any medical emergency,
              symptom, or treatment decision.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">3. Emergency features — limitations</h2>
            <p className="text-sm">
              The Emergency Script, ER Timeline, and any alerts or nudges within the app are <strong>assistants only</strong>.
              They must <strong>not</strong> be solely relied upon during a real medical emergency. In a life-threatening situation,
              call your local emergency number (999, 911, 112, etc.) immediately.
              We accept <strong>no liability</strong> for any outcome — including injury, harm, delay, or loss —
              arising from a user's reliance on the emergency features.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">4. Limitation of liability</h2>
            <p className="text-sm">
              The app is provided <strong>"as is"</strong> and <strong>"as available"</strong>, without warranties of any kind,
              express or implied. To the fullest extent permitted by law, we disclaim liability for:
            </p>
            <ul className="text-sm list-disc pl-6 mt-2 space-y-1">
              <li>Any errors, inaccuracies, or omissions in tracked or generated content.</li>
              <li>Loss of data, service interruptions, or technical failures.</li>
              <li>Decisions made by you, your carers, or medical professionals based on information stored or generated in the app.</li>
              <li>Any indirect, incidental, or consequential damages arising from your use of the app.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">5. Your data & storage</h2>
            <p className="text-sm">
              Your data is stored in a <strong>private database created specifically for this app</strong> at deployment.
              We do not sell your personal information. Uploaded photos and videos live in an object-storage bucket dedicated to this app.
              You can export your monthly PDF at any time and request account deletion by emailing us.
            </p>
            <p className="text-sm mt-2">
              See the{" "}
              <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link>
              {" "}for the full details.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">6. Payments & subscriptions</h2>
            <p className="text-sm">
              Paid Pro features are billed through Stripe. New subscribers receive a 7-day free trial; you may cancel at any time
              before the trial ends without being charged. Donations are non-refundable.
              Currency is auto-detected from your browser; you may switch it manually before checkout.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">7. Acceptable use</h2>
            <p className="text-sm">
              You agree not to misuse the app — including attempting to gain unauthorised access, uploading malicious content,
              or using the app on behalf of another person without their consent.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">8. Changes</h2>
            <p className="text-sm">
              We may update these Terms occasionally. Material changes will be announced in-app. Continued use after an update
              constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">9. Contact</h2>
            <p className="text-sm">
              Questions? Reach us at{" "}
              <a href="mailto:hello@letscure.app" className="underline underline-offset-2">hello@letscure.app</a>.
            </p>
          </section>
        </article>
      </div>
    </main>
  );
}
